import * as GAME from 'vibegame';
import { WorldTransform } from 'vibegame/transforms';
import { getScene } from 'vibegame/rendering';
import { LineSegments2 } from 'three/examples/jsm/lines/LineSegments2.js';
import { LineMaterial } from 'three/examples/jsm/lines/LineMaterial.js';
import { LineSegmentsGeometry } from 'three/examples/jsm/lines/LineSegmentsGeometry.js';

function srgbToLinear(c: number): number {
  return c < 0.04045 ? c * 0.0773993808 : Math.pow(c * 0.9478672986 + 0.0521327014, 2.4);
}

export const LineRenderer = GAME.defineComponent({
  endX: GAME.Types.f32,
  endY: GAME.Types.f32,
  endZ: GAME.Types.f32,
  color: GAME.Types.ui32,
  visible: GAME.Types.ui8,
});

const lineQuery = GAME.defineQuery([LineRenderer, WorldTransform]);

interface LineRenderContext {
  batchedLines: LineSegments2 | null;
  batchedGeometry: LineSegmentsGeometry | null;
  batchedMaterial: LineMaterial | null;
  lastResolution: { width: number; height: number };
  positionsArray: Float32Array | null;
  colorsArray: Float32Array | null;
}

const stateContexts = new WeakMap<GAME.State, LineRenderContext>();

function getContext(state: GAME.State): LineRenderContext {
  let ctx = stateContexts.get(state);
  if (!ctx) {
    ctx = {
      batchedLines: null,
      batchedGeometry: null,
      batchedMaterial: null,
      lastResolution: { width: 0, height: 0 },
      positionsArray: null,
      colorsArray: null,
    };
    stateContexts.set(state, ctx);
  }
  return ctx;
}

const LineRendererSystem: GAME.System = {
  group: 'draw',
  update(state) {
    const scene = getScene(state);
    if (!scene) return;

    const ctx = getContext(state);
    const entities = lineQuery(state.world);
    const visibleCount = entities.filter((eid) => LineRenderer.visible[eid] === 1).length;

    if (visibleCount === 0) {
      if (ctx.batchedLines) {
        ctx.batchedLines.visible = false;
      }
      return;
    }

    if (!ctx.batchedLines) {
      ctx.batchedGeometry = new LineSegmentsGeometry();
      ctx.batchedMaterial = new LineMaterial({
        vertexColors: true,
        worldUnits: false,
        linewidth: 1.5,
      });
      ctx.batchedLines = new LineSegments2(ctx.batchedGeometry, ctx.batchedMaterial);
      ctx.batchedLines.frustumCulled = false;
      scene.add(ctx.batchedLines);
    }

    ctx.batchedLines.visible = true;

    const width = window.innerWidth;
    const height = window.innerHeight;
    if (width !== ctx.lastResolution.width || height !== ctx.lastResolution.height) {
      ctx.batchedMaterial!.resolution.set(width, height);
      ctx.lastResolution = { width, height };
    }

    const positionsSize = visibleCount * 6;
    const colorsSize = visibleCount * 6;

    if (!ctx.positionsArray || ctx.positionsArray.length < positionsSize) {
      ctx.positionsArray = new Float32Array(positionsSize);
    }
    if (!ctx.colorsArray || ctx.colorsArray.length < colorsSize) {
      ctx.colorsArray = new Float32Array(colorsSize);
    }

    let idx = 0;
    for (const eid of entities) {
      if (LineRenderer.visible[eid] !== 1) continue;

      const startX = WorldTransform.posX[eid];
      const startY = WorldTransform.posY[eid];
      const startZ = WorldTransform.posZ[eid];
      const endX = LineRenderer.endX[eid];
      const endY = LineRenderer.endY[eid];
      const endZ = LineRenderer.endZ[eid];

      const colorVal = LineRenderer.color[eid];
      const r = srgbToLinear(((colorVal >> 16) & 0xff) / 255);
      const g = srgbToLinear(((colorVal >> 8) & 0xff) / 255);
      const b = srgbToLinear((colorVal & 0xff) / 255);

      const posIdx = idx * 6;
      ctx.positionsArray[posIdx] = startX;
      ctx.positionsArray[posIdx + 1] = startY;
      ctx.positionsArray[posIdx + 2] = startZ;
      ctx.positionsArray[posIdx + 3] = endX;
      ctx.positionsArray[posIdx + 4] = endY;
      ctx.positionsArray[posIdx + 5] = endZ;

      ctx.colorsArray[posIdx] = r;
      ctx.colorsArray[posIdx + 1] = g;
      ctx.colorsArray[posIdx + 2] = b;
      ctx.colorsArray[posIdx + 3] = r;
      ctx.colorsArray[posIdx + 4] = g;
      ctx.colorsArray[posIdx + 5] = b;

      idx++;
    }

    const positions = ctx.positionsArray.subarray(0, visibleCount * 6);
    const colors = ctx.colorsArray.subarray(0, visibleCount * 6);

    ctx.batchedGeometry!.setPositions(positions);
    ctx.batchedGeometry!.setColors(colors);
  },
  dispose(state) {
    const ctx = stateContexts.get(state);
    if (!ctx) return;

    if (ctx.batchedGeometry) {
      ctx.batchedGeometry.dispose();
    }
    if (ctx.batchedMaterial) {
      ctx.batchedMaterial.dispose();
    }
    stateContexts.delete(state);
  },
};

export const LineRendererPlugin: GAME.Plugin = {
  components: { LineRenderer },
  systems: [LineRendererSystem],
  config: {
    defaults: {
      'line-renderer': {
        endX: 0,
        endY: 0,
        endZ: 0,
        color: 0x000000,
        visible: 1,
      },
    },
  },
};
